import React, { lazy, Suspense } from "react";
import type { TFunction } from "i18next";
import { Navigate, Route, Routes } from "react-router-dom";
import {
    BarChart,
    LayoutDashboard,
    TicketCheck,
    UserCog,
    Users,
    Trophy,
    CreditCard,
    BookOpen,
    BrainCircuit,
    MoonStar,
    Handshake,
    BadgePercent,
    Flag,
    Brain,
    Bell,
} from "lucide-react";
import { Spin } from "antd";
import { RequirePermission } from "components/access-control/require-permission";

// Gate de UI (esconde a tela pra quem não tem a permissão) — o enforcement
// real de cada uma continua no backend (adaptRoute), mas a visibilidade no
// painel não pode depender disso: mesmo Base de Conhecimento (serviço
// AI-Agent separado, ainda sem guard nesse backend) e Curadoria IA (ainda sem
// endpoint real, só mock) ficam escondidas aqui — a pessoa sem permissão não
// pode ver a tela, o backend ganhar o guard correspondente é trabalho
// separado. Feature Flags aqui é só a tela ADMIN do painel (CRUD) — não mexe
// em `/feature-flags/all`, que é público e consumido pelo app.
const PERMISSION_BY_ROUTE_ID: Record<string, string> = {
    users: "user:manage",
    ticket: "ticket:manage",
    dreams: "dream:read",
    Gamification: "analytics:read",
    subscription: "subscription:write",
    affiliates: "marketing:manage",
    coupons: "marketing:manage",
    "events-analytics": "analytics:read",
    "ai-feedback": "analytics:read",
    "knowledge-ingestion": "content:manage",
    "curadoria-ia": "dream:moderate",
    "feature-flags": "system:manage",
    // Campanhas e catálogo de tipos de notificação (para os usuários do app).
    notification: "system:manage",
};

const UserIndexPage = lazy(() =>
    import("../pages/user/user-index/user-index-page").then((m) => ({
        default: m.UserIndexPage,
    })),
);
const UserCreateIndexPage = lazy(() =>
    import("../pages/user/user-create/user-create-index-page").then((m) => ({
        default: m.UserCreateIndexPage,
    })),
);
const DreamIndexPage = lazy(() =>
    import("../pages/dream/dream-index/dream-index-page").then((m) => ({
        default: m.DreamIndexPage,
    })),
);
const TeamIndexPage = lazy(() =>
    import("../pages/team/team-index/team-index-page").then((m) => ({
        default: m.TeamIndexPage,
    })),
);
const NotificationIndexPage = lazy(() =>
    import("../pages/notification/notification-index-page").then((m) => ({
        default: m.NotificationIndexPage,
    })),
);
const TicketIndexPage = lazy(() =>
    import("../pages/ticket/ticket-index-page").then((m) => ({
        default: m.TicketIndexPage,
    })),
);
const DashboardPage = lazy(
    () => import("pages/dashboard/dashboard-index-page"),
);
const KnowledgeIngestionPage = lazy(
    () => import("../pages/knowledge-ingestion/knowledge-ingestion-page"),
);
const GamificationPage = lazy(
    () => import("../pages/gamification/gamification-index-page"),
);
const ChatPage = lazy(() => import("../pages/chat/chat-index-page"));
const EventsAnalyticsPage = lazy(
    () => import("../pages/events-analytics/events-analytics-index-page"),
);
const SubscriptionPage = lazy(() =>
    import("../pages/subscription/subscription-index-page").then((m) => ({
        default: m.SubscriptionPage,
    })),
);
const FeatureFlagsPage = lazy(
    () => import("../pages/feature-flags/feature-flags-index-page"),
);
const AffiliatesPage = lazy(
    () => import("../pages/affiliates/affiliates-index-page"),
);
const CouponsPage = lazy(() => import("../pages/coupons/coupons-index-page"));
const CuradoriaPage = lazy(
    () => import("../pages/curadoria/curadoria-index-page"),
);
const AiFeedbackPage = lazy(
    () => import("../pages/ai-feedback/ai-feedback-page"),
);

export const routesMaster = (
    t: TFunction<"sidebar">,
    permissions: string[] = [],
    // Pré-RBAC: sem permissions vindas do backend, admin legado enxerga tudo.
    isLegacyAdmin = false,
) => {
    const hasAccess = (routeId: string): boolean => {
        const required = PERMISSION_BY_ROUTE_ID[routeId];
        return !required || isLegacyAdmin || permissions.includes(required);
    };

    return {
        location: [
            // Operação
            {
                path: "/dashboard",
                name: t("menu.dashboard"),
                id: "dashboard",
                group: t("groups.operations"),
                icon: <LayoutDashboard size={18} />,
            },
            {
                path: "/users",
                name: t("menu.users"),
                id: "users",
                group: t("groups.operations"),
                icon: <Users size={18} />,
            },
            {
                path: "/team",
                name: t("menu.team"),
                id: "team",
                group: t("groups.operations"),
                icon: <UserCog size={18} />,
            },
            {
                path: "/ticket",
                name: t("menu.ticket"),
                id: "ticket",
                group: t("groups.operations"),
                icon: <TicketCheck size={18} />,
            },
            {
                path: "/notification",
                name: t("menu.notifications"),
                id: "notification",
                group: t("groups.operations"),
                icon: <Bell size={18} />,
            },
            // Conteúdo & IA
            {
                path: "/dreams",
                name: t("menu.dreams"),
                id: "dreams",
                group: t("groups.content"),
                icon: <MoonStar size={18} />,
            },
            {
                path: "/gamification",
                name: t("menu.gamification"),
                id: "Gamification",
                group: t("groups.content"),
                icon: <Trophy size={18} />,
            },
            {
                path: "/knowledge-ingestion",
                name: t("menu.knowledgeBase"),
                id: "knowledge-ingestion",
                group: t("groups.content"),
                icon: <BookOpen size={18} />,
            },
            {
                path: "/curadoria",
                name: t("menu.curadoriaIA"),
                id: "curadoria-ia",
                group: t("groups.content"),
                icon: <BrainCircuit size={18} />,
            },
            // Monetização
            {
                path: "/subscription",
                name: t("menu.subscription"),
                id: "subscription",
                group: t("groups.monetization"),
                icon: <CreditCard size={18} />,
            },
            {
                path: "/affiliates",
                name: t("menu.affiliates"),
                id: "affiliates",
                group: t("groups.monetization"),
                icon: <Handshake size={18} />,
            },
            {
                path: "/coupons",
                name: t("menu.coupons"),
                id: "coupons",
                group: t("groups.monetization"),
                icon: <BadgePercent size={18} />,
            },
            // Análise & Sistema
            {
                path: "/events-analytics",
                name: t("menu.eventsAnalytics"),
                id: "events-analytics",
                group: t("groups.system"),
                icon: <BarChart size={18} />,
            },
            {
                path: "/ai-feedback",
                name: t("menu.aiFeedback"),
                id: "ai-feedback",
                group: t("groups.system"),
                icon: <Brain size={18} />,
            },
            {
                path: "/feature-flags",
                name: t("menu.featureFlags"),
                id: "feature-flags",
                group: t("groups.system"),
                icon: <Flag size={18} />,
            },
        ].filter((route) => hasAccess(route.id)),

        routes: (
            <Suspense
                fallback={
                    <div className="flex h-96 w-full items-center justify-center">
                        <Spin size="large" />
                    </div>
                }
            >
                <Routes>
                    <Route path={"/"} element={<DashboardPage />} />
                    <Route path={"/dashboard"} element={<DashboardPage />} />
                    <Route
                        path={"/users"}
                        element={
                            <RequirePermission permission="user:manage">
                                <UserIndexPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/admin"}
                        element={<Navigate to="/team" replace />}
                    />
                    <Route
                        path={"/events-analytics"}
                        element={
                            <RequirePermission permission="analytics:read">
                                <EventsAnalyticsPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/users/create"}
                        element={
                            <RequirePermission permission="user:manage">
                                <UserCreateIndexPage />
                            </RequirePermission>
                        }
                    />
                    <Route path={"/team"} element={<TeamIndexPage />} />
                    <Route
                        path={"/dreams"}
                        element={
                            <RequirePermission permission="dream:read">
                                <DreamIndexPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/notification"}
                        element={
                            <RequirePermission permission="system:manage">
                                <NotificationIndexPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/ticket"}
                        element={
                            <RequirePermission permission="ticket:manage">
                                <TicketIndexPage />
                            </RequirePermission>
                        }
                    />
                    <Route path={"/chat"} element={<ChatPage />} />
                    <Route
                        path={"/gamification"}
                        element={
                            <RequirePermission permission="analytics:read">
                                <GamificationPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/subscription"}
                        element={
                            <RequirePermission permission="subscription:write">
                                <SubscriptionPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/knowledge-ingestion"}
                        element={
                            <RequirePermission permission="content:manage">
                                <KnowledgeIngestionPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/feature-flags"}
                        element={
                            <RequirePermission permission="system:manage">
                                <FeatureFlagsPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/affiliates"}
                        element={
                            <RequirePermission permission="marketing:manage">
                                <AffiliatesPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/coupons"}
                        element={
                            <RequirePermission permission="marketing:manage">
                                <CouponsPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/curadoria"}
                        element={
                            <RequirePermission permission="dream:moderate">
                                <CuradoriaPage />
                            </RequirePermission>
                        }
                    />
                    <Route
                        path={"/ai-feedback"}
                        element={
                            <RequirePermission permission="analytics:read">
                                <AiFeedbackPage />
                            </RequirePermission>
                        }
                    />
                </Routes>
            </Suspense>
        ),
    };
};
